/**
 * App-wide confirmation prompts — the single, consistent way every web app asks
 * "are you sure?" before an action that is hard to undo.
 *
 * Wrap the app in `<ConfirmProvider>` (inside `AppThemeProvider`, alongside
 * `SnackbarProvider`), then call `useConfirm()` from anywhere below it:
 *
 *   const confirm = useConfirm();
 *   ...
 *   onClick={async () => {
 *     if (!await confirm({
 *       title: t('admin.companies.suspendTitle'),
 *       body: t('admin.companies.suspendBody', { name: row.legalName }),
 *       confirmLabel: t('admin.companies.suspend'),
 *       tone: 'danger',
 *     })) return;
 *     setStatus.mutate(...);
 *   }}
 *
 * **Why a promise rather than a `<ConfirmDialog>` per call site.** The dialog
 * component this replaces needed three pieces of state at every use — which
 * record is pending, whether the dialog is open, and what to run on confirm —
 * and the cost of that ceremony is why so many destructive actions never got a
 * prompt at all: suspending a company, revoking a citizen's access to their own
 * file, deleting a training material. Making the guard one `await` removes the
 * reason to skip it.
 *
 * Messages are passed in already localized — this component is i18n-agnostic,
 * exactly like {@link SnackbarProvider}.
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';

export interface ConfirmOptions {
  title: string;
  /**
   * What the action will do, in the reader's terms.
   *
   * Name the specific record and the consequence — "Suspend MES? Its officers
   * lose access to every municipality it works for" tells someone what they are
   * about to do; "Are you sure?" does not.
   */
  body: string;
  /**
   * The specific changes, one per line ("Front desk: On → Off"). Rendered as a
   * list under the body, so a save that changes three settings names all
   * three rather than asking about "your changes".
   */
  details?: string[];
  /** A consequence worth its own paragraph, shown after the details. */
  note?: string;
  /** Defaults to the caller's own wording; there is no sensible generic. */
  confirmLabel: string;
  cancelLabel: string;
  /**
   * `danger` paints the confirm button red — for actions that remove access,
   * destroy data, or cannot be undone. `default` is for the merely significant.
   */
  tone?: 'danger' | 'default';
}

/** Resolves true when the person confirms, false on cancel or dismiss. */
export type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

export function useConfirm(): ConfirmFn {
  const confirm = useContext(ConfirmContext);
  if (!confirm) throw new Error('useConfirm must be used within a ConfirmProvider');
  return confirm;
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<ConfirmOptions | null>(null);
  // Held in a ref rather than in state: settling the promise must not depend on
  // a re-render having happened, and the resolver is not rendered.
  const resolver = useRef<((confirmed: boolean) => void) | null>(null);

  const settle = useCallback((confirmed: boolean) => {
    resolver.current?.(confirmed);
    resolver.current = null;
    setPending(null);
  }, []);

  const confirm = useCallback<ConfirmFn>((options) => {
    // A second prompt while one is open would strand the first promise
    // unsettled, and its caller would wait forever. Refusing the new one keeps
    // the outstanding question on screen.
    if (resolver.current) return Promise.resolve(false);
    setPending(options);
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  const value = useMemo(() => confirm, [confirm]);

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      <Dialog open={pending !== null} onClose={() => settle(false)} maxWidth="xs" fullWidth>
        {pending && (
          <>
            <DialogTitle>{pending.title}</DialogTitle>
            <DialogContent>
              <DialogContentText>{pending.body}</DialogContentText>
              {pending.details && pending.details.length > 0 && (
                <Box
                  component="ul"
                  sx={{ mt: 1.5, mb: 0, pl: 2.5, color: 'text.primary', fontSize: 14 }}
                >
                  {pending.details.map((line) => (
                    <Box component="li" key={line} sx={{ mb: 0.5, overflowWrap: 'anywhere' }}>
                      {line}
                    </Box>
                  ))}
                </Box>
              )}
              {pending.note && (
                <DialogContentText sx={{ mt: 1.5 }}>{pending.note}</DialogContentText>
              )}
            </DialogContent>
            <DialogActions>
              <Button variant="text" onClick={() => settle(false)}>
                {pending.cancelLabel}
              </Button>
              <Button
                variant="contained"
                color={pending.tone === 'danger' ? 'error' : 'primary'}
                onClick={() => settle(true)}
                // Focus lands on the confirm button, but the dialog's own
                // Escape/backdrop close resolves false — so the cheapest way out
                // is always the one that changes nothing.
                autoFocus
              >
                {pending.confirmLabel}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </ConfirmContext.Provider>
  );
}
